import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { gtmTrack } from "../../utils/tracking";
import { QLINK_FUNNEL_EVENT, browserDimension, recordQLinkFunnelEvent } from "../../utils/qlink-funnel-events";
import { FREE_DAILY_ABSITE_QUESTIONS } from "../../constants/freeAllowance";
import { isReplayFeatureAvailable } from "../../utils/replay-consent";
import "./qlink-signin-teaser.scss";
import { REPLAY_MASK } from "../../utils/replay-mask";

// The signed-out QLink result card, shown once the public answer check has
// answered (so only after an answer). Two kinds:
// - curated: the check returned teaser data for this question (first sentence,
//   hook, locked sections, offer). Nothing curated ships in the bundle.
// - prompt: no curated data (every daily-post question today). The same card,
//   verdict and sign-in actions, with a plain "see why" sign-in prompt in place
//   of the curated content.
// Before an answer, while it is being checked, or when the check failed, the
// drawer keeps its signed-out wall.
//
// Leak rule: the ONLY explanation text in the DOM is the first sentence the
// server sent (curated only). The locked panel is aria-hidden skeleton bars
// plus an optional pre-blurred thumbnail; never blurred real text. The prompt
// holds no explanation text at all.

const LOCKED_LABEL = "Locked: the full explanation for this question. Sign in to read it.";

// GTM label for the uncurated card. The curated teaser's dataLayer payloads stay
// exactly as they were (no teaser_kind key), so existing tags read them as before.
export const TEASER_KIND_PROMPT = "prompt";

// qlink_auth_start from every teaser sign-in entry point (the Google button, the
// in-app "try Google anyway" path, the phone form).
export const trackTeaserAuthStart = (method, platform, kind) => gtmTrack("qlink_auth_start", {
  method,
  surface: "qlink_teaser",
  platform: platform || "unknown",
  ...(kind === TEASER_KIND_PROMPT ? { teaser_kind: TEASER_KIND_PROMPT } : {}),
});

// The prompt's copy: only what signing in actually gives (UX review
// 2026-10-31). The title answers the visitor's own question: why the right
// answer is right, or, after a correct answer, whether their reasoning was.
// ABSITE (the daily posts) adds the daily allowance the free plan states and,
// as a fact of its own and never as the reward for signing in, that a new
// free case is posted every day. Any other exam: no number this page cannot
// vouch for.
export const revealPromptCopy = ({ correct, correctLetter, examLabel }) => {
  let title = "See the full explanation";
  if (correct) {
    title = "Check your reasoning";
  } else if (correctLetter) {
    title = `See why ${correctLetter} is right`;
  }
  if (examLabel === "ABSITE") {
    return {
      title,
      body: `A free account unlocks the full explanation, plus ${FREE_DAILY_ABSITE_QUESTIONS} ABSITE practice questions a day.`,
      daily: "A new free ABSITE case is posted every day.",
    };
  }
  return { title, body: "A free account unlocks the full explanation.", daily: null };
};

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

// Google "G", unmodified four-colour mark (Google sign-in branding guidelines).
const GoogleMark = () => (
  <svg className="qlt-g" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const LockIcon = () => (
  <svg width="11" height="12" viewBox="0 0 11 12" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M2.5 5V3.5a3 3 0 0 1 6 0V5h.5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h.5zm1.5 0h3V3.5a1.5 1.5 0 0 0-3 0V5z" />
  </svg>
);

/**
 * @param result     {correct, correctAnswer, correctLetter, chosenLetter, teaser?}
 *                   teaser = {firstSentence, hook?, sections?, figureThumbnailUrl?, offer?};
 *                   without a firstSentence the card is the uncurated sign-in prompt
 * @param authMode   'google' | 'phone'
 * @param authPanel  the phone form (phone mode) or the in-app escape panel (Google
 *                   mode in a social webview); the phone form stays folded behind a
 *                   primary "Continue with phone number" button until chosen
 * @param modal      the phone sheet covers the page: dialog semantics + Escape
 * @param moveFocusOnOpen  false for arrow-key answers on the side drawer, so a
 *                   keyboard user is not pulled out of the option group
 * @param qlinkId, questionId  the shared link and question, for the
 *                   first-party teaser_shown funnel event only (no render use)
 */
const QLinkSigninTeaser = ({
  result,
  examLabel = "",
  authMode = "google",
  showGoogleButton = true,
  authPanel = null,
  onGoogle,
  onUsePhone,
  onUseGoogle,
  onBack,
  platform,
  questionKey,
  qlinkId,
  questionId,
  isOpen = true,
  modal = false,
  moveFocusOnOpen = true,
}) => {
  const headingRef = useRef(null);
  const phonePanelRef = useRef(null);
  const [phoneExpanded, setPhoneExpanded] = useState(false);
  const headingId = `qlt-verdict-${`${questionKey || "q"}`.replace(/[^A-Za-z0-9_-]/g, "")}`;
  const viewTrackedRef = useRef(null);
  const teaser = result?.teaser || {};
  // Curated content arrives only with a first sentence (the drawer's old gate).
  const curated = !!teaser.firstSentence;
  const kind = curated ? undefined : TEASER_KIND_PROMPT;
  const correct = result?.correct === true;
  const sections = curated && Array.isArray(teaser.sections) ? teaser.sections : [];
  const hasFigure = curated && !!teaser.figureThumbnailUrl;
  const offer = curated && teaser.offer && teaser.offer.dailyQuestions > 0 && teaser.offer.examType ? teaser.offer : null;
  const prompt = curated ? null : revealPromptCopy({ correct, correctLetter: result?.correctLetter, examLabel });
  const answerText = capitalize(result?.correctAnswer || "");
  const letterPrefix = result?.correctLetter ? `${result.correctLetter}. ` : "";

  // A11y: the drawer opened because of an answer, so move focus to the verdict
  // (role=status on content that mounts with the drawer is announced unreliably).
  // Not for arrow-key answers on the side drawer: the container announces the
  // verdict in a live region instead and leaves focus in the options.
  useEffect(() => {
    if (isOpen && moveFocusOnOpen && headingRef.current && typeof headingRef.current.focus === "function") {
      headingRef.current.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionKey, isOpen]);

  // Leaving phone mode folds the form again.
  useEffect(() => {
    if (authMode !== "phone") setPhoneExpanded(false);
  }, [authMode]);

  // The primary phone button unmounts when the form unfolds: bring the form into
  // view and put focus on its first field so it does not fall to <body>.
  useEffect(() => {
    const panel = phonePanelRef.current;
    if (!phoneExpanded || !panel) return;
    if (typeof panel.scrollIntoView === "function") panel.scrollIntoView({ block: "nearest" });
    // Skip hidden backing inputs (MUI Select renders an aria-hidden, tabIndex -1
    // native input for Country before the Phone Number field).
    const usable = ':not([aria-hidden="true"]):not([tabindex="-1"])';
    const field = panel.querySelector(`input[type="tel"]${usable}`)
      || panel.querySelector(`input:not([type=hidden])${usable}, textarea${usable}, button${usable}`);
    if (field && typeof field.focus === "function") field.focus({ preventScroll: true });
  }, [phoneExpanded]);

  // Counted on the first VISIBLE opening per question (a teaser that mounts in a
  // closed drawer is not a view).
  useEffect(() => {
    if (!isOpen || viewTrackedRef.current === questionKey) return;
    viewTrackedRef.current = questionKey;
    gtmTrack("qlink_teaser_view", {
      chosen_letter: result?.chosenLetter || "",
      is_correct: correct,
      has_figure: hasFigure,
      has_hook: curated && !!teaser.hook,
      platform: platform || "unknown",
      ...(kind ? { teaser_kind: kind } : {}),
    });
    recordQLinkFunnelEvent({
      qlinkId,
      questionId,
      platform: platform || "unknown",
      event: QLINK_FUNNEL_EVENT.TEASER_SHOWN,
      // kind only for the prompt: a curated teaser_shown stays exactly as it was
      // (every earlier row is curated, so no kind means curated).
      props: {
        variant: correct ? "correct" : "incorrect",
        figure: hasFigure ? "yes" : "no",
        browser: browserDimension(),
        ...(kind ? { kind } : {}),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionKey, isOpen]);

  const handleGoogle = () => {
    trackTeaserAuthStart("google", platform, kind);
    if (typeof onGoogle === "function") onGoogle();
  };

  // Phone starts on an explicit choice: "Continue with phone number" (the
  // primary action in phone mode, e.g. in-app browsers) or "Use phone number
  // instead" from Google mode. Either one unfolds the existing phone form.
  const startPhone = () => {
    trackTeaserAuthStart("phone", platform, kind);
    setPhoneExpanded(true);
  };
  const handleUsePhone = () => {
    if (typeof onUsePhone === "function") onUsePhone();
    startPhone();
  };

  const handleBack = () => {
    gtmTrack("qlink_back_to_question", { surface: "qlink_teaser", platform: platform || "unknown" });
    if (typeof onBack === "function") onBack();
  };

  const onKeyDown = (event) => {
    if (modal && event.key === "Escape") {
      event.stopPropagation();
      handleBack();
    }
  };

  const phoneMode = authMode === "phone";
  const showAuthPanel = !!authPanel && (!phoneMode || phoneExpanded);
  // The phone form is open: the prompt keeps its title only, so "Send code"
  // stays above an on-screen keyboard.
  const phoneOpen = phoneExpanded && showAuthPanel;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section
      className={`qlink-signin-teaser${correct ? " qlt-correct" : " qlt-wrong"}${phoneMode ? " qlt-compact" : ""}${curated ? "" : " qlt-uncurated"}${phoneOpen ? " qlt-phone-open" : ""}`}
      data-testid="qlink-signin-teaser"
      onKeyDown={onKeyDown}
      {...(modal
        ? { role: "dialog", "aria-modal": "true", "aria-labelledby": headingId }
        : { "aria-label": "Your result" })}
    >
      <div className="qlt-bar">
        <button type="button" className="qlt-back" onClick={handleBack} aria-label="Back to question">
          <span aria-hidden="true">‹</span> Question
        </button>
        <span className="qlt-bar-title">{examLabel ? `QLearn · ${examLabel} practice` : "QLearn practice"}</span>
      </div>

      <div className="qlt-body">
        <div className="qlt-verdict">
          <h2 className="qlt-verdict-line" id={headingId} tabIndex={-1} ref={headingRef}>
            <span className="qlt-v-icon" aria-hidden="true">{correct ? "✓" : "✕"}</span>
            {correct ? "Correct!" : "Not quite."}
          </h2>
          <p className="qlt-answer">
            {correct ? (
              <>{letterPrefix}<strong>{answerText}</strong></>
            ) : (
              <>Correct answer: <strong>{letterPrefix}{answerText}</strong></>
            )}
          </p>
        </div>

        {prompt && (
          <div className="qlt-prompt" data-testid="qlink-reveal-prompt">
            <h3 className="qlt-prompt-title">{prompt.title}</h3>
            <p className="qlt-prompt-body">{prompt.body}</p>
            {prompt.daily && <p className="qlt-prompt-daily">{prompt.daily}</p>}
          </div>
        )}

        {curated && teaser.hook && (
          <p className="qlt-hook">
            {!correct && result?.chosenLetter && (
              <span className="qlt-you">You chose {result.chosenLetter}. </span>
            )}
            {teaser.hook}
          </p>
        )}

        {curated && (
          <div className="qlt-first" {...REPLAY_MASK}>
            <div className="qlt-eyebrow">From the explanation</div>
            <div className="qlt-first-text">
              <ReactMarkdown>{teaser.firstSentence}</ReactMarkdown>
            </div>
          </div>
        )}

        {curated && <div className="qlt-locked" role="group" aria-label={LOCKED_LABEL}>
          {hasFigure && (
            <img className="qlt-thumb" src={teaser.figureThumbnailUrl} alt="" aria-hidden="true" />
          )}
          <div className="qlt-locked-right">
            <div className="qlt-skel" aria-hidden="true">
              <i /><i /><i /><i /><i /><i />
            </div>
            <div className="qlt-glass">
              <span className="qlt-lock-pill" aria-hidden="true"><LockIcon />Full explanation</span>
              {sections.length > 0 && (
                <>
                  <ul className="qlt-sections" aria-label="Sections in the full explanation">
                    {sections.map((name) => <li key={name}>{name}</li>)}
                  </ul>
                  <span className="qlt-sections-count" aria-hidden="true">
                    {sections.length} {sections.length === 1 ? "section" : "sections"}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>}

        {curated && (
          <p className="qlt-offer">
            {offer ? (
              <>
                <strong>Free account: full explanation + {offer.dailyQuestions} {offer.examType} questions a day.</strong>
                <span className="qlt-offer-rest">No card needed.</span>
              </>
            ) : (
              <strong>Sign in free to read the full explanation.</strong>
            )}
          </p>
        )}

        <div className="qlt-cta">
          {!phoneMode && showGoogleButton && (
            <button type="button" className="qlt-google" id="google_login_btn" onClick={handleGoogle}>
              <GoogleMark />
              <span>Continue with Google</span>
            </button>
          )}
          {phoneMode && !phoneExpanded && (
            <button type="button" className="qlt-phone-primary" onClick={startPhone}>
              Continue with phone number
            </button>
          )}
          {showAuthPanel && (
            <div className="qlt-auth-panel" ref={phonePanelRef}>
              {authPanel}
            </div>
          )}
          {phoneMode ? (
            <button type="button" className="qlt-alt" onClick={onUseGoogle}>
              Continue with Google instead
            </button>
          ) : (
            <button type="button" className="qlt-alt" onClick={handleUsePhone}>
              Use phone number instead
            </button>
          )}
        </div>

        {prompt && (
          <p className="qlt-reassure">No card needed. You’ll stay on this question after signing in.</p>
        )}

        {/* The sign-in agreement, as /login and the Daily page state it (new
            tabs, so the answered question is not lost); session recording only
            where it is built in. */}
        <p className="qlt-legal">
          By signing in, you agree to QLearn&apos;s{" "}
          <a href="/terms" rel="noopener noreferrer" target="_blank">
            Terms<span className="qlt-sr-only"> (opens in a new tab)</span>
          </a>
          {" "}and{" "}
          <a href="/privacy-policy" rel="noopener noreferrer" target="_blank">
            Privacy Policy<span className="qlt-sr-only"> (opens in a new tab)</span>
          </a>
          {isReplayFeatureAvailable() && (
            <>, including{" "}
              <a href="/privacy-policy#session-recording" rel="noopener noreferrer" target="_blank">
                session recording<span className="qlt-sr-only"> (opens in a new tab)</span>
              </a>
            </>
          )}.
        </p>
      </div>
    </section>
  );
};

export default QLinkSigninTeaser;
